import type {
	BaseEventPayload,
	DropTargetGetFeedbackArgs,
	DropTargetLocalizedData,
	ElementDragType,
} from "@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types";
import { uniqueArray } from "@msuite/utils/array";
import { moment } from "@msuite/utils/date";
import { getDocumentStructureFolderSize } from "@msuite/utils/documents";
import { getBucketPath } from "@msuite/utils/files";
import { generateId, getFileName, getFileSizeString, guardString } from "@msuite/utils/strings";
import type { DocumentStructureItem, Tables } from "@msuite/utils/types";
import React, { useRef, useState } from "react";
import { CollapseButton } from "../../button";
import { Draggable, Droppable } from "../../dnd";
import { useDisclosure } from "../../hooks";
import { Icon } from "../../icons";
import { useTranslation } from "../../localization";
import { toast } from "../../toast";
import { cn } from "../../utils";
import { useFileExplorerContext } from "./context";
import { FileExplorerFileCheckbox } from "./file-explorer-file-checkbox";
import { FileExplorerFolderCheckbox } from "./file-explorer-folder-checkbox";
import { FileExplorerItemSelect } from "./file-explorer-item-select";
import { flattenItems, selectionPasses } from "./shared";
import { useFileExplorerIcon } from "./use-file-explorer-icon";

/** Props Interface */
interface FileItemRowProps<T extends { file: Tables<"files"> }> {
	structureItem: DocumentStructureItem<T>;
	level: number;
	parentIds?: string[];
}

/** FileExplorerItemRow Component */
export function FileExplorerItemRow<T extends { file: Tables<"files"> }>({
	structureItem,
	level = 0,
	parentIds = [],
}: FileItemRowProps<T>) {
	/** Hooks */
	const disclosure = useDisclosure({ defaultIsOpen: true });
	const imperativeRef = useRef({
		toggleCheckbox: () => toast.error("Event handler not implemented"),
	});

	/** Context */
	const {
		selectedFiles,
		focusedItem,
		setFocusedItem,
		handleOnFolderDrop,
		handleOnAfterFileAction,
		selectColumns,
		filesById,
		canUpdateFile,
	} = useFileExplorerContext<T>();
	const { t } = useTranslation();

	const [dragItemId] = useState(generateId());

	/** Computed */
	const isFolder = structureItem.type === "folder";
	const icon = useFileExplorerIcon(structureItem, disclosure);
	const name = isFolder ? structureItem.name : getFileName(structureItem.file.fileName);
	const size = getFileSizeString(
		isFolder ? getDocumentStructureFolderSize(structureItem) : structureItem.file.size,
	);
	const createdAt = isFolder
		? guardString(null)
		: moment(structureItem.file.createdAt).format("LLLL");

	const isFocused = focusedItem?.id === structureItem.id;

	/** Functions */
	function handleOnClickItem(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
		const hasMetaKey = e.metaKey || e.ctrlKey;
		if (hasMetaKey) {
			imperativeRef.current.toggleCheckbox();
		} else {
			setFocusedItem(isFocused ? null : structureItem);
		}
	}

	function handleOnToggle() {
		if (isFolder) disclosure.onToggle();
	}

	function handleCanDrop(data: DropTargetGetFeedbackArgs<ElementDragType>) {
		const dragItem = data.source.data.item as {
			dragItemId: string;
			parentIds: string[];
			structureItem: DocumentStructureItem<T>;
		};
		if (dragItem.structureItem.type !== "folder") return true;
		if (structureItem.type === "folder") {
			if (structureItem.fullPath.includes(dragItem.structureItem.fullPath)) return false;
		}
		return true;
	}

	async function handleOnDrop(data: BaseEventPayload<ElementDragType> & DropTargetLocalizedData) {
		const selectedIds = selectedFiles;
		const dragItem = data.source.data.item as {
			dragItemId: string;
			parentIds: string[];
			structureItem: DocumentStructureItem<T>;
		};
		const payloadIds = getPayloadIds(dragItem);
		const ids = uniqueArray(payloadIds.concat(selectedIds)).filter(Boolean);
		// Moving a file rewrites its path, which the caller may do only where `canUpdateFile` says
		// so. A drag carrying one file it refuses is refused whole rather than moving the rest, the
		// same way a mixed batch delete is.
		if (!selectionPasses(ids, filesById, canUpdateFile)) {
			toast.error(t("files.cannot_move_files"));
			return;
		}
		let path =
			structureItem.type === "folder"
				? structureItem.fullPath
				: getBucketPath(structureItem.file.fullPath).cleanPath;

		if (dragItem.structureItem.type === "folder") {
			path = `${path}/${dragItem.structureItem.name}`;
		}
		await handleOnFolderDrop(path, ids);
		await handleOnAfterFileAction();
	}

	function getPayloadIds(dragItem: { structureItem: DocumentStructureItem<T> }) {
		if (dragItem.structureItem.type === "folder") {
			return flattenItems(dragItem.structureItem.items).map((item) => item.file.id);
		}
		return [dragItem.structureItem.file.id];
	}

	/** Render */
	return (
		<React.Fragment>
			<Droppable
				accept={["file-explorer-file", "file-explorer-folder"]}
				className="w-full"
				onClick={handleOnClickItem}
				onDoubleClick={handleOnToggle}
				dragOverIndicator={() => <div className="absolute inset-0 bg-accent-500/10" />}
				handleOnDrop={handleOnDrop}
				canDrop={handleCanDrop}
			>
				<Draggable
					className={cn(
						"w-full table-row file-explorer-row relative",
						isFocused ? "bg-accent-500/10" : "",
						"cursor-grab",
					)}
					type={isFolder ? "file-explorer-folder" : "file-explorer-file"}
					item={{
						dragItemId,
						parentIds,
						structureItem,
					}}
				>
					<div className="file-explorer-checkbox-column">
						{isFolder ? (
							<FileExplorerFolderCheckbox
								item={structureItem}
								imperativeRef={imperativeRef}
							/>
						) : (
							<FileExplorerFileCheckbox
								item={structureItem}
								imperativeRef={imperativeRef}
							/>
						)}
					</div>
					<div className="file-explorer-name-column h-stack">
						<div onClick={(e) => e.stopPropagation()}>
							<Icon name="GripVertical" />
						</div>
						<div
							className="h-stack"
							style={{ paddingLeft: `${level * 16}px` }}
						>
							{isFolder ? (
								<CollapseButton
									isOpen={disclosure.isOpen}
									onToggle={disclosure.onToggle}
									size="xxs"
									variant="plain"
									stopPropagation
								/>
							) : null}
							{icon}
							<p className="whitespace-nowrap">{name}</p>
						</div>
					</div>
					{selectColumns?.map((column) => (
						<div
							key={column.key}
							className="file-explorer-select-column"
							onClick={(e) => e.stopPropagation()}
						>
							{structureItem.type === "file" &&
							canUpdateFile(structureItem.file) &&
							(column.isEditable?.(structureItem) ?? true) ? (
								<FileExplorerItemSelect
									value={column.getValue(structureItem)}
									options={column.options}
									onChange={(value) => column.onChange(structureItem, value)}
								/>
							) : null}
						</div>
					))}
					<div className="file-explorer-size-column ">
						<p>{size}</p>
					</div>
					<div className="file-explorer-created-at-column">
						<p>{createdAt}</p>
					</div>
				</Draggable>
			</Droppable>
			{isFolder && disclosure.isOpen && structureItem.items.length > 0 ? (
				<React.Fragment>
					{structureItem.items.map((item) => (
						<FileExplorerItemRow
							key={item.id}
							structureItem={item}
							level={level + 1}
							parentIds={isFolder ? [...parentIds, dragItemId] : parentIds}
						/>
					))}
				</React.Fragment>
			) : null}
		</React.Fragment>
	);
}
